import Component from '../../js/_Component';

const CNAME = 'c-block-timeline';

export default class BlockTimeline extends Component {
	private items: NodeListOf<HTMLElement>;

	private progressSpacer: HTMLElement;

	private progressContainer: HTMLElement;

	private progressBar: HTMLElement;

	private lines: NodeListOf<HTMLElement>;

	private queued = false;

	constructor(element: HTMLElement) {
		super(element);

		this.items = element.querySelectorAll(`.${CNAME}--item`);
		this.progressSpacer = element.querySelector(`.${CNAME}--progress-width-spacer`);
		this.progressContainer = element.querySelector(`.${CNAME}--progress-container`);
		this.progressBar = element.querySelector(`.${CNAME}--progress-bar`);
		this.lines = element.querySelectorAll(`.${CNAME}--line`);

		// Set progress bar height and keep position updated while scrolling.
		this.progressBar.style.height = `${100 / this.items.length}%`;
		window.addEventListener('scroll', () => this.queueUpdate());

		// Set up line animations.
		this.configureLineAnimations();

		// Run once for cases where there is no scroll but is visible
		this.queueUpdate();
	}

	/**
	 * Creates an IntersectionObserver which observes all lines and trigger's a line's animation
	 * when it enters the viewport.
	 */
	configureLineAnimations() {
		/** The options passed in to the intersectionObserver. */
		const options = {
			/** Offset from the viewport's bounding box for the IntersectionObserver. */
			rootMargin: '-20%',
			/** Visibility percentage required to invoke the IntersectionObserver callback. */
			threshold: 0,
		};

		/** The IntersectionObserver whose callback will trigger the lines' animations. */
		const intersectionObserver = new IntersectionObserver((entries, observer) => {
			entries.forEach((entry) => {
				// Only continue if the item is in the viewport.
				if (!entry.isIntersecting) return;

				const line = entry.target as HTMLElement;

				// Trigger the line animation.
				line.style.transition = 'transform 500ms ease-out';
				line.style.transform = 'none';

				// Stop observing this line.
				observer.unobserve(line);
			});
		}, options);

		// Start observing the lines for intersection events.
		this.lines.forEach((line) => intersectionObserver.observe(line));
	}

	/**
	 * Sets the progress bar position to the index of the item whose vertical center is closest to
	 * the user's eyeline.
	 */
	setProgressBarPosition() {

		this.queued = false;

		var elementRect = this.element.getBoundingClientRect();
		var progressRect = this.progressContainer.getBoundingClientRect();
		//this.progressSpacer.style.setProperty('width', `${progressRect.width}px`); // This is only becomes relevant if the width of the container is changing dynamically. If it is explictly set in css no need to set.
		if (elementRect.bottom < (progressRect.bottom - progressRect.top)) { // Bottom of the element is above the bottom of the window, snap to bottom.
			this.progressContainer.style.setProperty('position', '');
			this.progressContainer.style.setProperty('top', '');
			this.progressContainer.style.setProperty('bottom', '0');
			this.progressContainer.style.setProperty('left', '');
		} else if (elementRect.top < 0) { // Top of the element is above the top of the window, set to fixed.
			this.progressContainer.style.setProperty('position', 'fixed');
			this.progressContainer.style.setProperty('top', '0');
			this.progressContainer.style.setProperty('bottom', '');
			this.progressContainer.style.setProperty('left', `${elementRect.left}px`);
		} else { // Top of the element is below top of the window, snap to top.
			this.progressContainer.style.setProperty('top', '0');
			this.progressContainer.style.setProperty('bottom', '');
			this.progressContainer.style.setProperty('position', '');
			this.progressContainer.style.setProperty('left', '');
		}

		/** The number of pixels from the top of the viewport which acts as the reference point. */
		const eyeline = window.innerHeight / 3;

		/** The array of distances, where the indices correspond to the item indices. */
		const distances = Array.from(this.items).map((item) => {
			const itemRect = item.getBoundingClientRect();
			const itemCenter = itemRect.top + (itemRect.height / 2);
			return Math.abs(eyeline - itemCenter);
		});

		/** The index of the item with the shortest distance. */
		const index = distances.indexOf(Math.min(...distances));

		// Update the progress bar position.
		this.progressBar.style.transform = `translateY(${index * 100}%)`;
	}

	queueUpdate() {
		if (!this.queued) {
			this.queued = true;
			requestAnimationFrame(() => this.setProgressBarPosition());
		}
	}
}

Component.register(BlockTimeline, 'BlockTimeline');
